import _ from 'lodash';
import { Box } from '@material-ui/core';
import { i18n } from '@lingui/core';
import { t, Trans } from '@lingui/macro';
import FormSection from '../../form/FormSection';
import { NgpLabelValue } from '../../form/NgpLabelValue';
import {
    AGENT_CREATED_AT_FIELD,
    AGENT_FIRMWARE_VERSION_FIELD,
    AGENT_LAST_SEEN_AT_FIELD,
    AGENT_LAST_VERSION_CHANGE_FIELD,
    AGENT_NETWORK_HOSTNAME_FIELD,
    AGENT_NETWORK_IPV4_LOCAL_FIELD,
    AGENT_NETWORK_IPV6_LOCAL_FIELD,
    AGENT_NETWORK_IP_EXTERNAL_FIELD,
    AGENT_OS_NAME_FIELD,
    AGENT_OS_VERSION_FIELD,
    AGENT_PUBLIC_IP_FIELD,
    AGENT_VITALS_APP_UPTIME,
    AGENT_VITALS_BATTERY_VOLTAGE,
    AGENT_VITALS_CERT_VALID_AFTER,
    AGENT_VITALS_CERT_VALID_BEFORE,
    AGENT_VITALS_CPU_LOAD,
    AGENT_VITALS_MEMORY_USED_PERCENT,
    AGENT_VITALS_POE_VOLTAGE,
    AGENT_VITALS_SD_AVG_ERASE_COUNT,
    AGENT_VITALS_SD_GOOD_BLOCK_RATIO,
    AGENT_VITALS_SD_LIFE_REMAINING,
    AGENT_VITALS_SD_MAX_ERASE_COUNT,
    AGENT_VITALS_SERIAL_NUMBER,
    AGENT_VITALS_SWITCH_IP,
    AGENT_VITALS_SWITCH_NAME,
    AGENT_VITALS_SWITCH_PORT,
    AGENT_VITALS_SWITCH_TYPE,
    AGENT_VITALS_SWITCH_VLAN,
    AGENT_VITALS_SYS_UPTIME,
    AGENT_VITALS_TEMPERATURE,
    AGENT_MODEL_LONG_DISPLAY_FIELD,
} from './AgentConstants';
import IPAddressUtilities from '../../../utilities/IPAddressUtilities';
import { dateTimeFormatter } from '../../../utilities';
import { DEFAULT_INVALID_VALUE } from '../../../utilities/FormattingUtilities';
import AgentReadOnlyGeoISP from './AgentReadOnlyGeoISP';
import {
    renderStatusData,
    renderVersionData,
    renderTxBudgetData,
    STATUS_LABEL_TEXT,
    VERSION_LABEL_TEXT,
} from './AgentReadOnlyHelper';
import { useColumnStyles } from './AgentStyles';
import { hasTxBudget, isRunning } from './AgentHelper';
import { durationDisplay, formatDatetime, roundVital } from './AgentDetailHelper';
import AgentReadOnlyWiFi from './AgentReadOnlyWiFi';

const hostLabelText = i18n._(t`Hostname`);
const externalIPLabelText = i18n._(t`External IP`);
const internalIPLabelText = i18n._(t`Internal IPv4`);
const publicIPLabelText = i18n._(t`Public IPv4`);
const ipv6Internal = i18n._(t`Internal IPv6`);
const osLabelText = i18n._(t`OS`);
const firstSeenLabelText = i18n._(t`First Seen`);
const lastSeenLabelText = i18n._(t`Last Seen`);
const lastVersionChangeLabelText = i18n._(t`Last Software Update`);
const serialNumberLabelText = i18n._(t`Serial Number`);
const appUptimeLabelText = i18n._(t`App Uptime`);
const sysUptimeLabelText = i18n._(t`System Uptime`);
const mTLSCertValidAfter = i18n._(t`mTLS Cert Valid From`);
const mTLSCertValidBefore = i18n._(t`mTLS Cert Valid To`);

const batteryVoltageLabelText = i18n._(t`Battery Voltage`);
const cpuLoadLabelText = i18n._(t`CPU Load`);
const memUsedLabelText = i18n._(t`Memory Used`);
const poeVoltageLabelText = i18n._(t`PoE Voltage`);
const sdLifeRemainingLabelText = i18n._(t`Storage Endurance`);
const sdGoodBlockRatioLabelText = i18n._(t`Storage Good Block Ratio`);
const sdMaxEraseCountLabelText = i18n._(t`Storage Maximum Erase`);
const sdAvgEraseCountLabelText = i18n._(t`Storage Average Erase`);
const temperatureLabelText = i18n._(t`Temperature`);
const firmwareVersionLabelText = i18n._(t`Firmware Version`);
const txBudgetUsedPercentText = t`Transmit Budget Used`;
const nPointModelText = t`Model`;

const switchTypeLabelText = i18n._(t`Switch xDP Type`);
const switchNameLabelText = i18n._(t`Switch Name`);
const switchIPLabelText = i18n._(t`Switch IP`);
const switchPortLabelText = i18n._(t`Switch Port`);
const switchVLANLabelText = i18n._(t`Switch VLAN`);

const readOnlyTopMargin = 1.5;

export default function AgentReadOnlySecondary({
    agent,
    isHardwareAgent,
    isHardware3kAgent,
    isWirelessAgent,
    isGlobalAgent,
    showExtendedInformation = false,
}) {
    const columnClasses = useColumnStyles();
    if (_.isEmpty(agent)) {
        return null;
    }

    // Doing this here because we don't want the field to show up if there isn't a value to show
    //  the formatter returns --
    const lastVersionChangeValue = _.get(agent, AGENT_LAST_VERSION_CHANGE_FIELD)
        ? dateTimeFormatter(_.get(agent, AGENT_LAST_VERSION_CHANGE_FIELD))
        : null;

    const renderedStatus = renderStatusData(columnClasses, agent, isHardware3kAgent.current);
    const renderedVersion = renderVersionData(columnClasses, agent, isHardware3kAgent.current);
    const osName = _.get(agent, AGENT_OS_NAME_FIELD, DEFAULT_INVALID_VALUE);
    const osVersion = _.get(agent, AGENT_OS_VERSION_FIELD, DEFAULT_INVALID_VALUE);
    const renderedOs = osName !== DEFAULT_INVALID_VALUE ? `${osName}, ${osVersion}` : osVersion;
    const renderTxBudget = renderTxBudgetData(columnClasses, agent, isGlobalAgent.current);
    // TODO: actually get model
    const longModel = _.get(agent, AGENT_MODEL_LONG_DISPLAY_FIELD, DEFAULT_INVALID_VALUE);

    return (
        <>
            <FormSection title={<Trans>System</Trans>}>
                <Box mt={readOnlyTopMargin}>
                    {showExtendedInformation && (
                        <>
                            <NgpLabelValue dataLabel={STATUS_LABEL_TEXT} dataValue={renderedStatus} />
                            <NgpLabelValue dataLabel={VERSION_LABEL_TEXT} dataValue={renderedVersion} />
                        </>
                    )}

                    <NgpLabelValue dataLabel={hostLabelText} dataValue={_.get(agent, AGENT_NETWORK_HOSTNAME_FIELD)} />

                    <NgpLabelValue dataLabel={nPointModelText} dataValue={longModel} showIfEmpty />

                    <NgpLabelValue
                        dataLabel={externalIPLabelText}
                        dataValue={_.get(agent, AGENT_NETWORK_IP_EXTERNAL_FIELD)}
                        showIfEmpty
                    />
                    <NgpLabelValue
                        dataLabel={internalIPLabelText}
                        dataValue={_.get(agent, AGENT_NETWORK_IPV4_LOCAL_FIELD)}
                        showIfEmpty
                    />
                    <NgpLabelValue dataLabel={publicIPLabelText} dataValue={_.get(agent, AGENT_PUBLIC_IP_FIELD)} />
                    <NgpLabelValue
                        dataLabel={ipv6Internal}
                        dataValue={IPAddressUtilities.formatIPv6(_.get(agent, AGENT_NETWORK_IPV6_LOCAL_FIELD))}
                    />
                    <NgpLabelValue dataLabel={osLabelText} dataValue={renderedOs} />
                    <NgpLabelValue
                        dataLabel={firstSeenLabelText}
                        dataValue={dateTimeFormatter(_.get(agent, AGENT_CREATED_AT_FIELD))}
                    />
                    <NgpLabelValue
                        dataLabel={lastSeenLabelText}
                        dataValue={dateTimeFormatter(_.get(agent, AGENT_LAST_SEEN_AT_FIELD))}
                    />
                    <NgpLabelValue dataLabel={lastVersionChangeLabelText} dataValue={lastVersionChangeValue} />
                    {isHardwareAgent.current && (
                        <>
                            <NgpLabelValue
                                dataLabel={serialNumberLabelText}
                                dataValue={_.get(agent, AGENT_VITALS_SERIAL_NUMBER)}
                            />
                            <NgpLabelValue
                                dataLabel={appUptimeLabelText}
                                dataValue={durationDisplay(_.get(agent, AGENT_VITALS_APP_UPTIME), agent)}
                                showIfEmpty
                            />
                            <NgpLabelValue
                                dataLabel={sysUptimeLabelText}
                                dataValue={durationDisplay(_.get(agent, AGENT_VITALS_SYS_UPTIME), agent)}
                                showIfEmpty
                            />

                            {showExtendedInformation && (
                                <>
                                    {!isHardware3kAgent.current && (
                                        <NgpLabelValue
                                            dataLabel={batteryVoltageLabelText}
                                            dataValue={roundVital(_.get(agent, AGENT_VITALS_BATTERY_VOLTAGE), 1, 'V')}
                                            showIfEmpty
                                        />
                                    )}
                                    <NgpLabelValue
                                        dataLabel={cpuLoadLabelText}
                                        dataValue={roundVital(_.get(agent, AGENT_VITALS_CPU_LOAD), 0, '%')}
                                        showIfEmpty
                                    />
                                    <NgpLabelValue
                                        dataLabel={memUsedLabelText}
                                        dataValue={roundVital(_.get(agent, AGENT_VITALS_MEMORY_USED_PERCENT), 0, '%')}
                                    />
                                    {!isHardware3kAgent.current && (
                                        <NgpLabelValue
                                            dataLabel={poeVoltageLabelText}
                                            dataValue={roundVital(_.get(agent, AGENT_VITALS_POE_VOLTAGE), 1, 'V')}
                                        />
                                    )}
                                    <NgpLabelValue
                                        dataLabel={sdLifeRemainingLabelText}
                                        dataValue={roundVital(_.get(agent, AGENT_VITALS_SD_LIFE_REMAINING), 0, '%')}
                                    />
                                    <NgpLabelValue
                                        dataLabel={sdGoodBlockRatioLabelText}
                                        dataValue={roundVital(_.get(agent, AGENT_VITALS_SD_GOOD_BLOCK_RATIO), 0, '%')}
                                    />
                                    <NgpLabelValue
                                        dataLabel={sdMaxEraseCountLabelText}
                                        dataValue={roundVital(_.get(agent, AGENT_VITALS_SD_MAX_ERASE_COUNT), 0, '')}
                                    />
                                    <NgpLabelValue
                                        dataLabel={sdAvgEraseCountLabelText}
                                        dataValue={roundVital(_.get(agent, AGENT_VITALS_SD_AVG_ERASE_COUNT), 0, '')}
                                    />
                                    <NgpLabelValue
                                        dataLabel={temperatureLabelText}
                                        dataValue={roundVital(_.get(agent, AGENT_VITALS_TEMPERATURE), 0, 'C')}
                                    />
                                    <NgpLabelValue
                                        dataLabel={firmwareVersionLabelText}
                                        dataValue={_.get(agent, AGENT_FIRMWARE_VERSION_FIELD)}
                                    />
                                </>
                            )}
                        </>
                    )}

                    <NgpLabelValue
                        dataLabel={mTLSCertValidAfter}
                        dataValue={formatDatetime(_.get(agent, AGENT_VITALS_CERT_VALID_AFTER))}
                    />
                    <NgpLabelValue
                        dataLabel={mTLSCertValidBefore}
                        dataValue={formatDatetime(_.get(agent, AGENT_VITALS_CERT_VALID_BEFORE))}
                    />

                    {isGlobalAgent.current && showExtendedInformation && isRunning(agent) && hasTxBudget(agent) && (
                        <NgpLabelValue dataLabel={txBudgetUsedPercentText} dataValue={renderTxBudget} showIfEmpty />
                    )}
                </Box>
            </FormSection>

            {!showExtendedInformation && <AgentReadOnlyGeoISP agent={agent} readOnlyTopMargin={readOnlyTopMargin} />}

            {showExtendedInformation && (
                <AgentReadOnlyWiFi
                    agent={agent}
                    readOnlyTopMargin={readOnlyTopMargin}
                    isWirelessAgent={isWirelessAgent}
                />
            )}

            {isHardwareAgent.current && (
                <FormSection title={<Trans>Switch Port</Trans>}>
                    <Box mt={readOnlyTopMargin}>
                        <NgpLabelValue
                            dataLabel={switchTypeLabelText}
                            dataValue={_.get(agent, AGENT_VITALS_SWITCH_TYPE)}
                            showIfEmpty
                        />
                        <NgpLabelValue
                            dataLabel={switchNameLabelText}
                            dataValue={_.get(agent, AGENT_VITALS_SWITCH_NAME)}
                            showIfEmpty
                        />
                        <NgpLabelValue
                            dataLabel={switchIPLabelText}
                            dataValue={IPAddressUtilities.formatIPAddress(_.get(agent, AGENT_VITALS_SWITCH_IP))}
                            showIfEmpty
                        />
                        <NgpLabelValue
                            dataLabel={switchPortLabelText}
                            dataValue={_.get(agent, AGENT_VITALS_SWITCH_PORT)}
                            showIfEmpty
                        />
                        <NgpLabelValue
                            dataLabel={switchVLANLabelText}
                            dataValue={_.get(agent, AGENT_VITALS_SWITCH_VLAN)}
                            showIfEmpty
                        />
                    </Box>
                </FormSection>
            )}
        </>
    );
}
